import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  ArrowUp,
  Bot,
  Eye,
  LogIn,
  PanelRightOpen,
  Plus,
  RefreshCw,
  RotateCcw,
  Settings,
  Square,
} from "lucide-react";
import type { DemoCaseResponse } from "@rnavoyager/contracts";
import { readDemoCase } from "./demo-api";
import { readDemoProgress, readPendingDemo, saveDemoProgress, savePendingDemo } from "./demo-state";
import { WorkspaceFileAccessContext } from "./WorkspaceFileAccess";
import { SessionFileReferencesContext, SessionMarkdown } from "./SessionFileReferences";
import { AutoGrowTextarea } from "./AutoGrowTextarea";
import { AssistantActivity } from "./AssistantActivity";
import { ComposerUploadButton } from "./ComposerAttachments";
import {
  WorkspaceSidebar,
  TaskWorkspaceHeader,
  ChatMessage,
  TaskResultCard,
  TaskPlanCardView,
} from "./ChatWorkspaceParts";
import { ConversationModelSelect } from "./ConversationModelSelect";
import { ResultWorkspaceRail } from "./ResultWorkspace";
import { DemoExecutionRail } from "./DemoExecutionRail";
import { DemoStartCue } from "./DemoStartCue";
import { demoFileAccess } from "./demo-files";
import "./DemoChat.css";

export const U1A_DEMO_PATH = "/chat/demo/u1a";
const compactQuery = "(max-width: 760px), (orientation: portrait) and (max-width: 1024px)";
// 阅读节奏与动态效果偏好无关，给各阶段留出查看结果的时间。
const stageDelay = [6000, 10000, 14000, 10000];
const planningDelay = 3000;

export default function DemoChat() {
  const [snapshot, setSnapshot] = useState<DemoCaseResponse | null>(null);
  const [seriesId, setSeriesId] = useState<string | null>(
    () => readPendingDemo()?.seriesId ?? null,
  );
  const [error, setError] = useState("");
  const [phase, setPhase] = useState(-2);
  const planning = phase === -3;
  const [playing, setPlaying] = useState(false);
  const [question, setQuestion] = useState(() => readPendingDemo()?.question ?? "");
  const [reload, setReload] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(() => !window.matchMedia?.(compactQuery).matches);
  const [railView, setRailView] = useState<"workspace" | "result">("workspace");
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [fileRequest, setFileRequest] = useState<{ path: string } | null>(null);
  const [showMethods, setShowMethods] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  const conversation = useRef<HTMLDivElement>(null);
  const following = useRef(true);
  const demo = snapshot?.case;
  const files = useMemo(() => (snapshot ? demoFileAccess(snapshot) : null), [snapshot]);
  const clearFileRequest = useCallback(() => setFileRequest(null), []);

  useEffect(() => {
    const controller = new AbortController();
    setError("");
    void readDemoCase("u1a", controller.signal)
      .then((value) => {
        if (controller.signal.aborted) return;
        const saved = readDemoProgress(value.caseDigest);
        setSnapshot(value);
        setPhase(saved);
        setPlaying(saved >= 0 && saved < 4);
      })
      .catch((cause) => {
        if (!controller.signal.aborted)
          setError(cause instanceof Error ? cause.message : "研究案例暂时不可用");
      });
    return () => controller.abort();
  }, [reload]);
  useEffect(() => {
    const media = window.matchMedia?.(compactQuery);
    if (!media) return;
    const resize = () => {
      if (media.matches) setRailOpen(false);
    };
    media.addEventListener?.("change", resize);
    return () => media.removeEventListener?.("change", resize);
  }, []);
  useEffect(() => {
    if (!snapshot || !planning) return;
    const timer = setTimeout(() => {
      setPhase(-1);
      saveDemoProgress(snapshot.caseDigest, -1);
    }, planningDelay);
    return () => clearTimeout(timer);
  }, [snapshot, planning]);
  useEffect(() => {
    if (!snapshot || !playing || phase < 0 || phase >= 4) return;
    const timer = setTimeout(() => {
      const next = phase + 1;
      setPhase(next);
      saveDemoProgress(snapshot.caseDigest, next);
      if (next === 4) setPlaying(false);
    }, stageDelay[phase]);
    return () => clearTimeout(timer);
  }, [snapshot, phase, playing]);
  useEffect(() => {
    if (following.current) end.current?.scrollIntoView?.({ block: "end", behavior: "auto" });
  }, [phase]);

  function advance(next: number) {
    if (!snapshot) return;
    setPhase(next);
    setPlaying(next >= 0 && next < 4);
    saveDemoProgress(snapshot.caseDigest, next);
    following.current = true;
  }
  function openResult(id: string) {
    setSelectedDocumentId(id);
    setRailView("result");
    setRailOpen(true);
  }
  function openExecution() {
    setRailView("workspace");
    setRailOpen(true);
  }
  function openFile(path: string) {
    setFileRequest({ path });
    openExecution();
  }
  function continueResearch(value: string) {
    if (!snapshot || !value.trim()) return;
    try {
      const existing = readPendingDemo();
      savePendingDemo({
        caseId: snapshot.case.caseId,
        caseDigest: snapshot.caseDigest,
        question: value.trim(),
        ...(seriesId ? { seriesId } : {}),
        requestId:
          existing?.caseDigest === snapshot.caseDigest &&
          (!existing.seriesId || existing.seriesId === seriesId)
            ? existing.requestId
            : `demo_${crypto.randomUUID()}`,
      });
      window.location.assign("/login");
    } catch {
      setError("浏览器无法保存后续问题，请允许此站点使用会话存储。");
    }
  }
  const documents =
    demo?.stages.slice(0, Math.max(0, phase + 1)).map((stage) => stage.document) ?? [];
  const selected =
    documents.find((document) => document.documentId === selectedDocumentId) ?? documents.at(-1);
  const summaryBlock = demo?.stages[4].document.blocks.find(
    (block) => block.kind === "markdown" && block.markdown.includes("主要结论："),
  );
  const summary =
    summaryBlock?.kind === "markdown"
      ? summaryBlock.markdown
          .split("主要结论：")[1]!
          .replaceAll("**", "")
          .replace(/^\s*-\s+/gm, "")
          .replaceAll("quality_notes", "结构质量")
          .trim()
      : demo?.summary;
  const activityLabel = planning
    ? "正在规划任务"
    : demo && playing && phase >= 0 && phase < 4
      ? demo.stages[phase + 1].title
      : null;
  const initial = phase === -2;
  const content = (
    <div className="app-shell demo-workspace">
      <WorkspaceSidebar
        collapsed={sidebarCollapsed}
        mobileOpen={mobileSidebarOpen}
        onToggleCollapsed={() => setSidebarCollapsed((value) => !value)}
        onToggleMobile={() => setMobileSidebarOpen((value) => !value)}
      >
        <button className="new-task" onClick={() => window.location.assign("/login")}>
          <Plus size={17} />
          <span>新任务</span>
        </button>
        <nav className="session-list" aria-label="会话">
          <div className="session-row active">
            <button className="session-select" onClick={() => setMobileSidebarOpen(false)}>
              <span className="session-title-viewport" title={demo?.title}>
                <span className="session-title">U1A × U1 snRNA</span>
              </span>
            </button>
          </div>
        </nav>
        <div className="sidebar-footer">
          <button
            className="sidebar-action"
            onClick={() => {
              advance(-2);
              setQuestion("");
              setMobileSidebarOpen(false);
            }}
          >
            <RotateCcw size={16} />
            <span>重新开始</span>
          </button>
          <a className="sidebar-action" href="/">
            返回主页
          </a>
          <div className="account-chip">
            <span className="account-email">未登录</span>
            <div className="account-actions">
              <a className="icon-button" aria-label="登录" title="登录" href="/login">
                <LogIn size={16} />
              </a>
            </div>
          </div>
        </div>
      </WorkspaceSidebar>
      <main className="main-area">
        <div
          className={`task-workspace-layout ${railOpen ? "rail-open" : "rail-closed"}${railOpen && railView === "result" ? " result-open" : ""}`}
        >
          <div className="task-shell">
            <TaskWorkspaceHeader
              title={demo?.title ?? "U1A × U1 snRNA"}
              identity="序列与构象如何影响识别"
              actions={
                <>
                  {!railOpen && (
                    <button
                      aria-label="打开任务执行与文件树菜单"
                      className="icon-button framed"
                      onClick={openExecution}
                      title="打开任务执行与文件树"
                    >
                      <PanelRightOpen size={17} />
                    </button>
                  )}
                  {phase === 4 && demo && (
                    <button
                      className="icon-button framed"
                      aria-label="查看研究报告"
                      title="查看研究报告"
                      onClick={() => openResult(demo.stages[4].document.documentId)}
                    >
                      <Eye size={17} />
                    </button>
                  )}
                  <button
                    className="icon-button framed"
                    aria-label="任务设置"
                    title="任务设置"
                    onClick={() => setShowMethods((value) => !value)}
                  >
                    <Settings size={17} />
                  </button>
                </>
              }
            />
            {!railOpen && (phase >= -1 || planning) && (
              <button
                aria-label="打开任务执行详情"
                className={`task-execution-peek ${playing || planning ? "running" : phase === 4 ? "success" : "pending"}`}
                onClick={openExecution}
                type="button"
              >
                <Activity size={15} />
                <span>
                  <small>任务执行</small>
                  <strong>{activityLabel ?? (phase === 4 ? "任务已完成" : "等待开始执行")}</strong>
                </span>
              </button>
            )}
            {showMethods && demo && (
              <section className="settings-panel demo-method-panel">
                <h2>方法与来源</h2>
                <p>
                  本案例使用提前完成的真实计算结果，按研究阶段展示。计算预测不代表实验亲和力或生物活性。
                </p>
                <ul>
                  {demo.sources.map((source) => (
                    <li key={source.url}>
                      <a href={source.url} target="_blank" rel="noopener noreferrer">
                        {source.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <div
              className="conversation"
              ref={conversation}
              onScroll={() => {
                const view = conversation.current;
                if (view)
                  following.current = view.scrollHeight - view.scrollTop - view.clientHeight < 120;
              }}
            >
              {!demo ? (
                <section className="welcome-card">
                  <Bot size={28} />
                  <h2>探索 RNA 的序列特异性识别</h2>
                  {error ? (
                    <>
                      <p role="alert">{error}</p>
                      <button className="secondary" onClick={() => setReload((value) => value + 1)}>
                        重新加载
                      </button>
                    </>
                  ) : (
                    <p role="status">正在读取研究案例…</p>
                  )}
                </section>
              ) : (
                <>
                  {initial && (
                    <section className="welcome-card">
                      <Bot size={28} />
                      <h2>我们先从哪里开始呢？</h2>
                      <p>研究问题已准备好，我们一起看看序列与结构如何影响识别。</p>
                    </section>
                  )}
                  <div className="timeline">
                    {(phase >= -1 || planning) && (
                      <ChatMessage role="user" label="你">
                        <SessionMarkdown sessionId="public-demo">{demo.goal}</SessionMarkdown>
                      </ChatMessage>
                    )}
                    {phase === -1 && (
                      <section aria-label="开始执行计划" className="conversation-interaction">
                        <TaskPlanCardView
                          objective={demo.goal}
                          preview="把序列识别与茎部构象分开比较，沿着证据检验假设。"
                          steps={demo.stages.map((stage, index) => ({
                            id: stage.stageId,
                            kind: index === 4 ? "report" : index === 0 ? "inspect" : "tool",
                            description: stage.title,
                            acceptanceCriteria: stage.explanation,
                          }))}
                          assumptions={demo.hypotheses}
                          awaitingStart
                          status={{ label: "等待开始", tone: "pending" }}
                          busy={false}
                          startHint={<DemoStartCue stage="plan" />}
                          startDescriptionId="demo-plan-hint"
                          onStart={() => advance(0)}
                          onDiscard={() => advance(-2)}
                        />
                      </section>
                    )}
                    {demo.stages.slice(0, Math.max(0, phase + 1)).map((stage, index) => (
                      <TaskResultCard
                        key={stage.stageId}
                        document={stage.document}
                        inlineFeatured={index === 2 && !(railOpen && railView === "result")}
                        onOpenResult={openResult}
                        sessionId="public-demo"
                        summary={stage.explanation}
                        publicDemo
                      />
                    ))}
                    {phase === 4 && (
                      <ChatMessage role="system" label="RNAVoyager">
                        <h2>沿着这个问题，继续探索</h2>
                        <p>{summary}</p>
                        <div className="demo-follow-up-options">
                          {demo.followUpQuestions.map((item) => (
                            <button
                              className="secondary"
                              key={item}
                              onClick={() => setQuestion(item)}
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                        <details>
                          <summary>方法与来源</summary>
                          <p>
                            本案例使用提前完成的真实计算结果，按研究阶段展示。结果为计算预测，不代表实验亲和力或生物活性。
                          </p>
                          <ul>
                            {demo.sources.map((source) => (
                              <li key={source.url}>
                                <a href={source.url} target="_blank" rel="noopener noreferrer">
                                  {source.title}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </details>
                      </ChatMessage>
                    )}
                  </div>
                  <AssistantActivity label={activityLabel} />
                  {error && demo && (
                    <div className="error-banner" role="alert">
                      {error}
                    </div>
                  )}
                  <div ref={end} />
                </>
              )}
            </div>
            <form
              className="composer"
              onSubmit={(event) => {
                event.preventDefault();
                if (!demo || planning) return;
                if (initial) advance(-3);
                else continueResearch(question);
              }}
            >
              <ComposerUploadButton
                disabled
                disabledReason="登录后可上传文件，继续研究。"
                onFiles={() => undefined}
              />
              <AutoGrowTextarea
                aria-label={initial ? "任务目标" : "你的后续问题"}
                value={initial ? (demo?.goal ?? "") : question}
                readOnly={initial}
                disabled={!demo || planning}
                maxRows={8}
                onChange={(event) => setQuestion(event.target.value)}
                placeholder={initial ? "正在准备研究问题…" : "给 RNAVoyager 发消息"}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    event.currentTarget.form?.requestSubmit();
                  }
                }}
              />
              {initial && demo && <DemoStartCue stage="explore" />}
              <ConversationModelSelect
                options={null}
                seriesId={seriesId}
                menuDirection="up"
                disabled
                emptyLabel="登录后选择模型"
                onChange={setSeriesId}
              />
              {playing && (
                <button
                  aria-label="停止任务"
                  className="primary send stop"
                  type="button"
                  onClick={() => setPlaying(false)}
                >
                  <Square fill="currentColor" size={11} />
                </button>
              )}
              {!playing && phase >= 0 && phase < 4 && (
                <button
                  aria-label="继续任务"
                  className="primary send resume"
                  type="button"
                  onClick={() => setPlaying(true)}
                >
                  <RefreshCw size={15} />
                </button>
              )}
              <button
                aria-label="发送消息"
                aria-describedby={initial && demo ? "demo-explore-hint" : undefined}
                className="primary send"
                type="submit"
                disabled={!demo || planning || (!initial && !question.trim())}
              >
                <ArrowUp size={18} />
              </button>
            </form>
          </div>
          {railOpen &&
            demo &&
            (railView === "result" && selected ? (
              <ResultWorkspaceRail
                key={selected.documentId}
                documents={[selected]}
                finalized
                live={false}
                onClose={() => setRailOpen(false)}
                onOpenWorkspace={openExecution}
                onUseSelection={(selection) =>
                  setQuestion(`请基于这个结果继续分析：${JSON.stringify(selection)}`)
                }
                sessionId="public-demo"
                publicDemo
              />
            ) : (
              <DemoExecutionRail
                demo={demo}
                phase={phase}
                playing={playing}
                onClose={() => setRailOpen(false)}
                onOpenResult={openResult}
                fileRequest={fileRequest}
                onFileRequestHandled={clearFileRequest}
              />
            ))}
        </div>
      </main>
    </div>
  );
  return files && demo ? (
    <WorkspaceFileAccessContext.Provider value={files}>
      <SessionFileReferencesContext.Provider
        value={{
          artifacts: documents.flatMap((document) =>
            document.blocks.flatMap((block) => (block.kind === "artifacts" ? block.items : [])),
          ),
          openFile,
        }}
      >
        {content}
      </SessionFileReferencesContext.Provider>
    </WorkspaceFileAccessContext.Provider>
  ) : (
    content
  );
}
